import { CheckSmartBillingOptions, MonthlyBillingEligibility } from 'types/smartBillingOptions.ts'
import { isActiveContract } from './contracts.ts'
import {
  COUNTER_TYPES,
  METERING_REGIMES,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCT_TYPES,
  PRODUCTS,
  PRODUCTS_CONFIG,
  REGIONS,
  ServiceContract,
  ServiceContractElectricity,
  ServiceContractGas,
  TIMEFRAME_CODES
} from '@boltenergy-be/shared'
import { EventLog, EventLogType, MeteringUpdateEventLog } from 'types/eventLog.ts'
import { selectEventsByEventLogType } from 'store/queries/bolt-api/contacts/selectors.ts'
import { UserTypes } from 'store/auth/types.ts'
import { useGetUserEventLogsQuery } from 'store/queries/bolt-api/events/index.ts'
import { PendingMeteringChange } from 'pages/app/addresses/offtake-address/types.ts'
import dayjs from 'dayjs'
import { DATE_FORMAT } from 'constants/constants.ts'

/**
 * Check if the contract has a night exclusive meter
 * @param {ServiceContract} contract
 * @returns {boolean | undefined}
 */
export const checkHasNightExclusiveMeter = (contract: ServiceContract) => {
  return contract.deliveryPoint?.previousIndex?.registers?.some((register) => register.timeframeCode === TIMEFRAME_CODES.nightExclusive)
}

/**
 * Check if metering regime is R1/R3 (per month or per quarter hour)
 * @param {ServiceContract} contract
 * @returns {boolean}
 */
export const checkHasR1R3MeteringRegime = (contract: ServiceContract) => {
  return [METERING_REGIMES.perMonth, METERING_REGIMES.perQuarterHour].some((regime) => regime === contract?.deliveryPoint?.meteringRegime)
}

/**
 * Check if the contract has smart meter options
 *
 * @param {CheckSmartBillingOptions['contract']} contract
 * @returns {boolean}
 */
export const checkHasSmartMeterOptionsForContract = (contract?: ServiceContract): boolean => {
  if (!contract) return false

  const isNightExclusiveMeter = checkHasNightExclusiveMeter(contract)
  const isSmartMeterPossible = checkHasR1R3MeteringRegime(contract)

  return !isNightExclusiveMeter && isSmartMeterPossible
}

/**
 * Check if the meterDetails from the contract flow has smart meter options
 * @param {CheckSmartBillingOptions['meterDetails']} meterDetails
 * @param {CheckSmartBillingOptions['region']} region
 * @returns {boolean}
 */
export const checkHasSmartMeterOptionsForMeterDetails = (
  meterDetails: CheckSmartBillingOptions['meterDetails'],
  region: CheckSmartBillingOptions['region']
): boolean => {
  if (!meterDetails) return false
  return meterDetails.counterType === COUNTER_TYPES.digital && !meterDetails.hasExclNight && region === REGIONS.flanders
}

/**
 * Check if a electricity contract is eligible for dynamic tariff. !Important: Gas contracts are not eligible for dynamic tariff due to only having a day price for gas.
 * @param {CheckSmartBillingOptions} props
 * @returns {boolean}
 */
export const checkIsDynamicTariffEligible = ({ serviceContracts, product, meterDetails, region }: CheckSmartBillingOptions): boolean => {
  // Dynamic tariff is not available for Bolt Fix
  if (!product || PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.type] === PRODUCT_TYPES.fix) return false

  // Check meterDetails for dynamic tariff eligibility (coming from contract flow)
  if (meterDetails) {
    return checkHasSmartMeterOptionsForMeterDetails(meterDetails, region)
  }

  // Check contract for dynamic tariff eligibility (Only electricity, gas is never eligible)
  return checkHasSmartMeterOptionsForContract(serviceContracts.electricity)
}

/**
 * Check if the contract is eligible for monthly billing
 * @param {CheckSmartBillingOptions} props
 * @returns {boolean}
 */
export const checkIsMonthlyBillingEligible = ({
  serviceContracts,
  product,
  meterDetails,
  needsGas,
  region
}: CheckSmartBillingOptions): MonthlyBillingEligibility => {
  // Dynamic tariff is not available for Bolt Go
  if (!product || product === PRODUCTS.go)
    return {
      electricity: false,
      gas: false
    }

  // Check meterDetails for monthly billing eligibility (coming from contract flow)
  if (meterDetails) {
    const isEligible = checkHasSmartMeterOptionsForMeterDetails(meterDetails, region)
    return {
      electricity: isEligible,
      gas: needsGas ? isEligible : false
    }
  }

  // Check contract for monthly billing eligibility
  const { electricity, gas } = serviceContracts
  return {
    electricity: checkHasSmartMeterOptionsForContract(electricity),
    gas: gas ? checkHasSmartMeterOptionsForContract(gas) : false
  }
}

/**
 * Check if the contract has monthly billing
 * @param {MonthlyBillingEligibility} eligibility
 * @param {ServiceContractElectricity} electricityContract
 * @param {ServiceContractGas} gasContract
 * @returns {boolean | undefined}
 */
export const checkIsMonthlyBilling = (
  eligibility: MonthlyBillingEligibility,
  electricityContract: ServiceContractElectricity,
  gasContract?: ServiceContractGas
): boolean | undefined => {
  return (
    eligibility.electricity &&
    electricityContract.detail.monthlyBilling &&
    (gasContract && isActiveContract(gasContract) && eligibility.gas ? gasContract.detail.monthlyBilling : true)
  )
}

/**
 * Gets the event logs of type METER_CONFIG_UPDATED for the contact and selects the relevant data for pending metering changes
 * @param {string | undefined} email
 * @param {UserTypes | null} userType
 * @returns {EventLog[] | undefined}
 */
export const useGetMeterConfigUpdatedEventLogs = (email: string | undefined, userType: UserTypes | null) => {
  const { userEventLogsData } = useGetUserEventLogsQuery(
    { email: email || '' },
    {
      skip: userType !== UserTypes.SUPER_USER || !email,
      selectFromResult: ({ data }) => ({
        userEventLogsData: selectEventsByEventLogType({
          events: data?.logs,
          type: EventLogType.METER_CONFIG_UPDATED
        })
      })
    }
  )
  return userEventLogsData
}

/**
 * Function to determine whether an event log is generic or of type MeteringUpdateEventLog
 *
 * @param {unknown} eventLog
 * @returns {eventLog is MeteringUpdateEventLog}
 */
export const isMeteringEventLog = (eventLog: unknown): eventLog is MeteringUpdateEventLog => {
  return (eventLog as MeteringUpdateEventLog).type === EventLogType.METER_CONFIG_UPDATED
}

/**
 * Function to get pending metering changes (monthly billing and dynamic tariff) for a given contract number based on the event logs of type METER_CONFIG_UPDATED
 * @param {EventLog[]} userEventLogsData
 * @param {string} contractNumber
 * @returns {PendingMeteringChange | undefined}
 */
export const getPendingMeteringChanges = (userEventLogsData: EventLog[], contractNumber: string): PendingMeteringChange | undefined => {
  if (userEventLogsData && userEventLogsData.every((event) => isMeteringEventLog(event))) {
    // Check if there are events with a due date in the future belonging to the current electricity contract
    const elMeterUpdates = userEventLogsData
      .filter(
        (event) =>
          event.data?.serviceContractId === contractNumber && event.data?.eventDueDate && dayjs(event.data.eventDueDate).isAfter(dayjs())
      )
      .sort((a, b) => dayjs(b.data.eventDueDate).diff(dayjs(a.data.eventDueDate)))

    // Find the monthly billing and dynamic tariff updates and select the most recent one (there should only be one of each, but just in case)
    const monthlyBillingUpdates = elMeterUpdates.filter((event) => event.data?.newMeteringConfiguration.monthlyBilling)[0]?.data

    const dynamicTariffUpdates = elMeterUpdates.filter((event) => event.data?.newMeteringConfiguration.dynamicTariff)[0]?.data

    return {
      monthlyBilling: monthlyBillingUpdates
        ? {
            active: monthlyBillingUpdates.newMeteringConfiguration.monthlyBilling,
            dueDate: dayjs(monthlyBillingUpdates.eventDueDate).format(DATE_FORMAT)
          }
        : undefined,
      dynamicTariff: dynamicTariffUpdates
        ? {
            active: dynamicTariffUpdates.newMeteringConfiguration.dynamicTariff,
            dueDate: dayjs(dynamicTariffUpdates.eventDueDate).format(DATE_FORMAT)
          }
        : undefined
    }
  }
  return undefined
}
